import axios from 'axios';

const DEFAULT_API_BASE =
  'https://app-itmaster-prod-g4gqhbdtbrekfnhy.northeurope-01.azurewebsites.net/live';

/** Django mounts the staff API at `.../live/`. Append `/live` when env points at site root (common 404 cause). */
function normalizeApiBaseUrl(raw: string | undefined): string {
  const fromEnv = raw?.trim();
  const base = (fromEnv && fromEnv.length > 0 ? fromEnv : DEFAULT_API_BASE).replace(/\/+$/, '');
  if (base.endsWith('/live')) {
    return base;
  }
  return `${base}/live`;
}

const baseURL = normalizeApiBaseUrl(process.env.REACT_APP_API_BASE_URL);

const AxiosInstance = axios.create({
    baseURL: baseURL,
    withCredentials: true,
    timeout: 5000,
    headers: {
        'Content-Type': 'application/json',
    }
});

AxiosInstance.interceptors.request.use(
    (config) => {
      const token = localStorage.getItem("jwt");
      if (token) {
        config.headers.Authorization = `Bearer ${token}`;
      }
      return config;
    },
    (error) => {
      return Promise.reject(error);
    }
);

AxiosInstance.interceptors.response.use(
    (response) => {
        return response;
    },
    (error) => {
        const url = error.config?.url ?? '';
        const isJwtObtain =
          /(^|\/)token\/?$/.test(url.split('?')[0]) && !url.includes('refresh');
        if (error.response?.status === 401 && !isJwtObtain) {
            localStorage.clear();
            window.location.href = '/login';
        }
        return Promise.reject(error);
    }
);

export interface RefreshParentChildrenResponse {
    status: string;
    parent_id: number;
    parent_booking_id: number;
    last_child_sync_at?: string;
    function_status_code?: number;
    message?: string;
}

/** Response from refresh endpoint: status 202 = accepted (sync in background), 200 = sync completed inline (legacy). */
export const refreshParentChildrenBookings = async (
    parentPk: string | number
): Promise<{ status: number; data: RefreshParentChildrenResponse }> => {
    const response = await AxiosInstance.post(
        `/parent/${parentPk}/refresh-children/`,
        {},
        { timeout: 15000 }
    );
    return { status: response.status, data: response.data as RefreshParentChildrenResponse };
};

const POLL_INTERVAL_MS = 2000;
const POLL_MAX_WAIT_MS = 6 * 60 * 1000; // 6 minutes

/** Poll parent until sync_status is not 'in_progress'. Returns final parent or throws on timeout. */
export const pollParentSyncStatus = async (
    parentPk: string | number
): Promise<{ sync_status: string; sync_error?: string }> => {
    const start = Date.now();
    while (Date.now() - start < POLL_MAX_WAIT_MS) {
        const res = await AxiosInstance.get(`/parent/${parentPk}/`);
        const parent = res.data;
        if (parent.sync_status !== "in_progress") {
            return { sync_status: parent.sync_status, sync_error: parent.sync_error };
        }
        await new Promise((r) => setTimeout(r, POLL_INTERVAL_MS));
    }
    throw new Error("Refresh timed out. Check back in a moment.");
};

export default AxiosInstance;
